import DOMPurify from 'dompurify';
import type { ReviewContext } from './reviewContext';
import { readVideoAdjustments, type VideoAdjustments } from './videoAdjustments';

export interface VideoCommentFrame {
  id: string;
  time: number;
  frame?: number;
  imageUrl: string;
  attachmentId: number;
}
export interface VideoComment extends ReviewContext {
  videoAdjustments?: VideoAdjustments;
  videoAdjustmentsSavedAt?: number;
  id: string;
  time: number;
  frame?: number;
  color: string;
  text: string;
  imageUrl: string;
  attachmentId: number;
  totalFrames?: number;
  voiceUrl?: string;
  voiceAttachmentId?: number;
  /** Drawings belong to frames; text/voice/context belong to this one comment. */
  frames?: VideoCommentFrame[];
}
export interface VideoFrameDraft {
  id: string;
  time: number;
  frame?: number;
  /** New/repainted frame. Omit to retain existing without another upload. */
  file?: File;
  existing?: VideoCommentFrame;
}
export type VideoCommentDraft = Omit<VideoComment, 'imageUrl' | 'attachmentId' | 'frames'>;
export interface VideoDraftActions {
  save: (file: File, comment: VideoCommentDraft, previous?: VideoComment, voice?: File | null) => Promise<VideoComment>;
  saveGroup?: (frames: VideoFrameDraft[], comment: VideoCommentDraft, previous?: VideoComment, voice?: File | null) => Promise<VideoComment>;
  remove: (comment: VideoComment) => Promise<void>;
  send?: () => void | Promise<void>;
  canSend?: boolean;
}
export const MAX_VIDEO_COMMENTS = 80;
export const MAX_VIDEO_COMMENT_FRAMES = 80;
export const MAX_VIDEO_COMMENTS_JSON_LENGTH = 1_000_000;
export function videoCommentFrames(comment: VideoComment): VideoCommentFrame[] {
  return comment.frames?.length ? comment.frames : [{ id: comment.id, time: comment.time, frame: comment.frame, imageUrl: comment.imageUrl, attachmentId: comment.attachmentId }];
}
/** Preserve the first-frame fields for already deployed single-frame readers. */
export function withVideoCommentFrames(comment: VideoCommentDraft, frames: VideoCommentFrame[]): VideoComment {
  if (!frames.length) throw new Error('В комментарии должен остаться хотя бы один кадр.');
  const ordered = [...frames].sort((a, b) => a.time - b.time || a.id.localeCompare(b.id));
  return { ...comment, ...ordered[0], id: comment.id, frames: ordered };
}
export function videoCommentFrameLabel(groupIndex: number, frameIndex: number, count: number): string {
  return count > 1 ? `${groupIndex + 1}.${frameIndex + 1}` : String(groupIndex + 1);
}
export function createVideoCommentId(): string {
  // Unlike randomUUID(), getRandomValues() also works on the LAN's http://tab.
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, value => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
export function safeMediaUrl(value: unknown): value is string {
  if (typeof value !== 'string' || !value || value.length > 4096) return false;
  try { return ['http:', 'https:'].includes(new URL(value, window.location.href).protocol); } catch { return false; }
}
function readFrame(value: unknown, fallbackId?: string): VideoCommentFrame | null {
  if (!value || typeof value !== 'object') return null;
  const item = value as Record<string, unknown>;
  const id = typeof item.id === 'string' ? item.id : fallbackId;
  if (!id || id.length > 100 || typeof item.time !== 'number' || !Number.isFinite(item.time) || item.time < 0 || !safeMediaUrl(item.imageUrl) || typeof item.attachmentId !== 'number' || !Number.isSafeInteger(item.attachmentId) || item.attachmentId <= 0) return null;
  return { id, time: item.time, frame: typeof item.frame === 'number' && Number.isSafeInteger(item.frame) && item.frame > 0 ? item.frame : undefined, imageUrl: item.imageUrl, attachmentId: item.attachmentId };
}
export function readVideoComments(raw?: string): VideoComment[] {
  if (!raw || raw.length > MAX_VIDEO_COMMENTS_JSON_LENGTH) return [];
  try {
    const data: unknown = JSON.parse(raw);
    if (!Array.isArray(data)) return [];
    const ids = new Set<string>();
    return data.slice(0, MAX_VIDEO_COMMENTS).flatMap((value: unknown): VideoComment[] => {
      if (!value || typeof value !== 'object') return [];
      const item = value as Record<string, unknown>;
      if (typeof item.id !== 'string' || !item.id || item.id.length > 100 || ids.has(item.id)) return [];
      const frameIds = new Set<string>();
      const frames = Array.isArray(item.frames) ? item.frames.slice(0, MAX_VIDEO_COMMENT_FRAMES).flatMap(value => {
        const frame = readFrame(value);
        if (!frame || frameIds.has(frame.id)) return [];
        frameIds.add(frame.id);
        return [frame];
      }).sort((a, b) => a.time - b.time || a.id.localeCompare(b.id)) : undefined;
      const first = frames?.[0] || readFrame(item, item.id);
      if (!first) return [];
      ids.add(item.id);
      const context: ReviewContext = {};
      const adjustments = readVideoAdjustments(item.videoAdjustments);
      const view = adjustments ? {videoAdjustments: adjustments, ...(typeof item.videoAdjustmentsSavedAt === 'number' && Number.isSafeInteger(item.videoAdjustmentsSavedAt) && item.videoAdjustmentsSavedAt > 0 ? {videoAdjustmentsSavedAt:item.videoAdjustmentsSavedAt} : {})} : {};
      for (const key of ['author','recipient','project','regulation','stage','asset'] as const) if (typeof item[key] === 'string') context[key] = item[key].slice(0, 500);
      const voice = safeMediaUrl(item.voiceUrl) && typeof item.voiceAttachmentId === 'number' && Number.isSafeInteger(item.voiceAttachmentId) && item.voiceAttachmentId > 0 ? {voiceUrl:item.voiceUrl, voiceAttachmentId:item.voiceAttachmentId} : {};
      return [{ ...first, id: item.id, color: typeof item.color === 'string' && /^#[0-9a-f]{6}$/i.test(item.color) ? item.color : '#ff4545', text: typeof item.text === 'string' ? item.text.slice(0, 4000) : '', ...context, ...voice, ...view, ...(frames?.length ? { frames } : {}), ...(typeof item.totalFrames === 'number' && Number.isSafeInteger(item.totalFrames) && item.totalFrames > 0 ? {totalFrames:item.totalFrames} : {}) }];
    }).sort((a, b) => a.time - b.time || a.id.localeCompare(b.id));
  } catch { return []; }
}
export function videoCommentAttachments(html: string): number[] {
  const root = document.createElement('div'); root.innerHTML = DOMPurify.sanitize(html);
  return [...root.querySelectorAll<HTMLElement>('video[data-video-comments]')].flatMap(video => readVideoComments(video.dataset.videoComments).flatMap(commentAttachmentIds));
}
export function videoCommentGroups(html: string): Record<number, number[]> {
  const root = document.createElement('div'); root.innerHTML = DOMPurify.sanitize(html);
  return Object.fromEntries([...root.querySelectorAll<HTMLElement>('video[data-video-comments][data-attachment-id]')].map(video => [Number(video.dataset.attachmentId), readVideoComments(video.dataset.videoComments).flatMap(commentAttachmentIds)]));
}
export function commentAttachmentIds(comment: VideoComment): number[] {
  return [...new Set([...videoCommentFrames(comment).map(frame => frame.attachmentId), ...(comment.voiceAttachmentId ? [comment.voiceAttachmentId] : [])])];
}
